"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CurrencyField,
  frequencyOptions,
  SCHEDULE_KEYS,
  useMoney,
} from "@/components/tools/finance/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { currencyDigits } from "@/lib/tool-kit/finance/currency";
import {
  buildSchedule,
  type LoanInput,
  type LoanSchedule,
  loanFromPayment,
  yearlySummary,
} from "@/lib/tool-kit/finance/loan";
import { roundTo } from "@/lib/tool-kit/finance/tvm";

/** English strings. A translated content file overrides these through `ui`. */
export const LOAN_PAYMENT_CALCULATOR_STRINGS = {
  inputsHeading: "Your loan",
  find: "Find",
  findPayment: "The payment for a loan amount",
  findAmount: "The loan amount a payment can repay",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  principal: "Loan amount",
  payment: "Payment each period",
  rate: "Annual interest rate (%)",
  termYears: "Term (years)",
  termMonths: "Extra months",
  perYear: "Payments are made",
  extra: "Extra paid with each payment",
  annually: "Annually (1 per year)",
  semiannually: "Semiannually (2 per year)",
  quarterly: "Quarterly (4 per year)",
  bimonthly: "Every two months (6 per year)",
  monthly: "Monthly (12 per year)",
  semimonthly: "Twice a month (24 per year)",
  biweekly: "Every two weeks (26 per year)",
  weekly: "Weekly (52 per year)",
  daily: "Daily (365 per year)",
  continuous: "Continuously",
  convention:
    "Fixed rate, interest charged each period on the remaining balance at the annual rate ÷ payments per year, payments at the end of each period. The payment and each period's interest are rounded to the smallest unit of the currency.",
  resultsHeading: "Results",
  paymentResult: "Payment each period",
  loanAmountResult: "Loan amount",
  totalPaid: "Total of all payments",
  totalInterest: "Total interest",
  paymentsMade: "Payments made",
  paymentsCount: "one:{count} payment|other:{count} payments",
  payoffYears: "Payoff time (years)",
  interestSaved: "Interest saved by paying extra",
  paymentsSaved: "Payments saved by paying extra",
  finalPaymentNote:
    "The last payment is {final} rather than {regular}: with every payment rounded, the final one clears whatever balance is left.",
  neverPaysOff:
    "That payment does not even cover the first period's interest, so the balance would never fall. Enter a larger payment.",
  scheduleHeading: "Amortization schedule",
  viewLabel: "Show the schedule",
  viewYearly: "By year",
  viewEvery: "Every payment",
  copySchedule: "Copy schedule",
  copied: "Copied!",
  yearColumn: "Year",
  numberColumn: "Payment",
  paidColumn: "Paid",
  interestColumn: "Interest",
  principalColumn: "Principal",
  extraColumn: "Extra",
  balanceColumn: "Balance",
  invalidAmount: "Enter an amount greater than zero.",
  invalidRate: "Enter an interest rate between 0 and 100.",
  invalidTerm: "Enter a term of up to 50 years.",
} as const;

type Strings = typeof LOAN_PAYMENT_CALCULATOR_STRINGS;
type Mode = "payment" | "amount";
type View = "yearly" | "every";

interface Form {
  mode: Mode;
  principal: string;
  payment: string;
  rate: string;
  years: string;
  months: string;
  perYear: string;
  extra: string;
}

const MAX_YEARS = 50;

interface Parsed {
  input: LoanInput | null;
  error: string | null;
}

function parseLoan(form: Form, currency: string, strings: Strings): Parsed {
  const digits = currencyDigits(currency);
  const rate = parseNumberInput(form.rate);
  const years = parseNumberOr(form.years, 0);
  const months = parseNumberOr(form.months, 0);
  const perYear = Number(form.perYear);
  const totalYears = years + months / 12;
  const typed = parseNumberInput(
    form.mode === "payment" ? form.principal : form.payment
  );
  if (typed === null || typed <= 0) {
    return { input: null, error: strings.invalidAmount };
  }
  if (rate === null || rate < 0 || rate > 100) {
    return { input: null, error: strings.invalidRate };
  }
  if (totalYears <= 0 || totalYears > MAX_YEARS) {
    return { input: null, error: strings.invalidTerm };
  }
  const periods = Math.max(1, Math.round(totalYears * perYear));
  const extra = Math.max(0, parseNumberOr(form.extra, 0));
  const base = {
    annualRate: rate / 100,
    paymentsPerYear: perYear,
    periods,
    extra,
    digits,
  };
  if (form.mode === "payment") {
    return { input: { ...base, principal: typed }, error: null };
  }
  const principal = roundTo(
    loanFromPayment(typed, base.annualRate, perYear, periods),
    digits
  );
  return { input: { ...base, principal, payment: typed }, error: null };
}

function scheduleText(schedule: LoanSchedule, strings: Strings): string {
  const head = [
    strings.numberColumn,
    strings.paidColumn,
    strings.interestColumn,
    strings.principalColumn,
    strings.extraColumn,
    strings.balanceColumn,
  ].join("\t");
  const lines = schedule.rows.map((r) =>
    [r.n, r.paid, r.interest, r.principal, r.extra, r.balance].join("\t")
  );
  return [head, ...lines].join("\n");
}

function ScheduleTable({
  schedule,
  input,
  view,
  strings,
  currency,
}: {
  schedule: LoanSchedule;
  input: LoanInput;
  view: View;
  strings: Strings;
  currency: string;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  if (view === "yearly") {
    return (
      <DataTable
        head={[
          strings.yearColumn,
          strings.paidColumn,
          strings.interestColumn,
          strings.principalColumn,
          strings.balanceColumn,
        ]}
        rows={yearlySummary(schedule.rows, input.paymentsPerYear).map((y) => [
          fmt(y.year),
          money(y.paid),
          money(y.interest),
          money(y.principal),
          money(y.balance),
        ])}
      />
    );
  }
  return (
    <div className="max-h-[32rem] overflow-y-auto rounded-lg">
      <DataTable
        head={[
          strings.numberColumn,
          strings.paidColumn,
          strings.interestColumn,
          strings.principalColumn,
          strings.extraColumn,
          strings.balanceColumn,
        ]}
        rows={schedule.rows.map((r) => [
          fmt(r.n),
          money(r.paid),
          money(r.interest),
          money(r.principal),
          money(r.extra),
          money(r.balance),
        ])}
      />
    </div>
  );
}

function Results({
  input,
  mode,
  currency,
  strings,
}: {
  input: LoanInput;
  mode: Mode;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const money = useMoney(currency);
  const [view, setView] = useState<View>("yearly");
  const schedule = buildSchedule(input);
  if (schedule.neverPaysOff) {
    return <Notice>{strings.neverPaysOff}</Notice>;
  }
  const baseline =
    input.extra > 0 ? buildSchedule({ ...input, extra: 0 }) : null;
  const count = schedule.rows.length;
  const minorUnit = 10 ** -input.digits;
  const finalDiffers =
    Math.abs(schedule.finalPayment - schedule.payment) >= minorUnit / 2 &&
    input.extra === 0;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={
            mode === "payment"
              ? strings.paymentResult
              : strings.loanAmountResult
          }
          value={money(mode === "payment" ? schedule.payment : input.principal)}
        />
        <StatCard label={strings.totalPaid} value={money(schedule.totalPaid)} />
        <StatCard
          label={strings.totalInterest}
          value={money(schedule.totalInterest)}
        />
        <StatCard
          label={strings.paymentsMade}
          value={tPlural(strings.paymentsCount, locale, count, {
            count: fmt(count),
          })}
        />
        <StatCard
          label={strings.payoffYears}
          value={fmt(count / input.paymentsPerYear, {
            maximumFractionDigits: 2,
          })}
        />
        {baseline ? (
          <StatCard
            label={strings.interestSaved}
            value={money(baseline.totalInterest - schedule.totalInterest)}
          />
        ) : null}
        {baseline ? (
          <StatCard
            label={strings.paymentsSaved}
            value={fmt(baseline.rows.length - count)}
          />
        ) : null}
      </StatGrid>
      {finalDiffers ? (
        <Notice>
          {t(strings.finalPaymentNote, {
            final: money(schedule.finalPayment),
            regular: money(schedule.payment),
          })}
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h3 className={WIDGET_H3}>{strings.scheduleHeading}</h3>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copySchedule}
          text={scheduleText(schedule, strings)}
        />
      </div>
      <PillGroup
        label={strings.viewLabel}
        name="lp-view"
        onChange={setView}
        options={[
          { value: "yearly", label: strings.viewYearly },
          { value: "every", label: strings.viewEvery },
        ]}
        value={view}
      />
      <ScheduleTable
        currency={currency}
        input={input}
        schedule={schedule}
        strings={strings}
        view={view}
      />
    </section>
  );
}

export default function LoanPaymentCalculatorWidget() {
  const strings = useToolUi(LOAN_PAYMENT_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    mode: "payment",
    principal: "200000",
    payment: "1500",
    rate: "6",
    years: "30",
    months: "0",
    perYear: "12",
    extra: "0",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const parsed = parseLoan(form, currency, strings);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <PillGroup
          label={strings.find}
          name="lp-mode"
          onChange={(value) => update({ mode: value })}
          options={[
            { value: "payment", label: strings.findPayment },
            { value: "amount", label: strings.findAmount },
          ]}
          value={form.mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="lp-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          {form.mode === "payment" ? (
            <TextField
              id="lp-principal"
              label={strings.principal}
              numeric
              onChange={(value) => update({ principal: value })}
              value={form.principal}
            />
          ) : (
            <TextField
              id="lp-payment"
              label={strings.payment}
              numeric
              onChange={(value) => update({ payment: value })}
              value={form.payment}
            />
          )}
          <TextField
            id="lp-rate"
            label={strings.rate}
            numeric
            onChange={(value) => update({ rate: value })}
            value={form.rate}
          />
          <SelectField
            id="lp-per-year"
            label={strings.perYear}
            onChange={(value) => update({ perYear: value })}
            options={frequencyOptions(strings, SCHEDULE_KEYS)}
            value={form.perYear}
          />
          <TextField
            id="lp-years"
            label={strings.termYears}
            numeric
            onChange={(value) => update({ years: value })}
            value={form.years}
          />
          <TextField
            id="lp-months"
            label={strings.termMonths}
            numeric
            onChange={(value) => update({ months: value })}
            value={form.months}
          />
          <TextField
            id="lp-extra"
            label={strings.extra}
            numeric
            onChange={(value) => update({ extra: value })}
            value={form.extra}
          />
        </div>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {parsed.input ? (
        <Results
          currency={currency}
          input={parsed.input}
          mode={form.mode}
          strings={strings}
        />
      ) : (
        <Notice>{parsed.error}</Notice>
      )}
    </div>
  );
}
